// ink-component
import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { bemHelper } from '../../../helpers';

import { getCheckboxId } from './utils';

import getGlobalProps from '../../../helpers/constants/global-props';
import useDeprecatedWarning from '../../../hooks/useDeprecatedWarning';

export type ChildType = {
  type: {
    displayName: string;
  };
  props: {
    id: string;
  };
};

export interface DeprecatedDropdownItemProps {
  /** Contents */
  children?: ReactNode;
  /** Modifier for header item */
  isHeader?: boolean;
  /** Modifier for active item */
  isActive?: boolean;
  /** Modifier for item with a top divider */
  hasTopDivider?: boolean;
  /** Modifier for disabled state */
  isDisabled?: boolean;
  /** Centralizes the content */
  isCentered?: boolean;
  /** onClick callback */
  onClick?: (event: Event) => void;
  /** An URL or a URL fragment */
  href?: string;
  /** Html target; requires href to be specified */
  target?: string;
  /** Html rel; requires href to be specified */
  rel?: string;
  /** Modifier for nowrap content */
  noWrap?: boolean;
  /** Disables hover effect */
  noHover?: boolean;
  /** Modifier for React Router */
  withReactRouter?: boolean;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
}

const globalProps = getGlobalProps({ deprecated: true, inkSass: true });

/**
 * @deprecated This component is deprecated and replaced by [Dropdown](https://ink.carta.com/components/Dropdown/) and [DropdownItem](https://ink.carta.com/components/Dropdown.Item/)
 * @status deprecated
 * @replacementComponent Dropdown.Item
 * @dateOfDeletion July 1st, 2023
 */

function DeprecatedDropdownItem(props: DeprecatedDropdownItemProps) {
  const {
    isHeader,
    hasTopDivider,
    isActive,
    isDisabled,
    noWrap,
    children,
    onClick,
    withReactRouter,
    href,
    target,
    rel,
    noHover,
    isCentered,
  } = props;
  useDeprecatedWarning({ componentName: 'DeprecatedDropdownItem', substitute: 'Dropdown.Item' });

  const defineClassName = () => {
    const b = bemHelper();

    return b('ink-dropdown--item', {
      header: isHeader,
      divider: hasTopDivider,
      active: isActive,
      disabled: isDisabled,
      nowrap: noWrap,
      nohover: noHover,
      centered: isCentered,
    });
  };

  const handleClick = e => {
    e.stopPropagation();
    onClick && onClick(e);
  };

  const handleKeyPress = e => {
    e.stopPropagation();
    if (e.key === 'Enter' || e.keyCode === 13) {
      const { childNodes } = e.target;
      if (childNodes[0] && childNodes[0].click) {
        childNodes[0].click();
      }
    }
  };

  const renderContent = () => {
    if (withReactRouter) {
      return (
        <Link className={defineClassName()} to={href} onClick={handleClick}>
          {children}
        </Link>
      );
    }
    if (href) {
      return (
        <a tabIndex={-1} className={defineClassName()} href={href} target={target} rel={rel}>
          {children}
        </a>
      );
    }
    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
      <label htmlFor={getCheckboxId(children)} className={defineClassName()} onClick={handleClick}>
        {children}
      </label>
    );
  };

  return (
    <li
      className="ink-dropdown--list-item"
      role="menuitem"
      tabIndex={isHeader ? -1 : 0}
      onKeyPress={handleKeyPress}
      data-testid={props['data-testid']}
      {...globalProps}
    >
      {renderContent()}
    </li>
  );
}

DeprecatedDropdownItem.defaultProps = {
  children: null,
  isHeader: false,
  isActive: false,
  hasTopDivider: false,
  isDisabled: false,
  isCentered: false,
  onClick: () => {},
  href: null,
  noWrap: false,
  noHover: false,
  target: null,
  rel: null,
  withReactRouter: false,
  'data-testid': undefined,
};

DeprecatedDropdownItem.displayName = 'DeprecatedDropdownItem';

export { DeprecatedDropdownItem as DeprecatedDropdownItemDefault };

export default DeprecatedDropdownItem;
